import React, { forwardRef, useEffect, useImperativeHandle, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  Checkbox,
  Grid,
  Paper,
  LinearProgress,
  Typography,
  makeStyles
} from "@material-ui/core";

import { getCreativeTypeLabel, parseIdList } from "../../utils/dataUtils";
import {
  fetchAssignedCreatives,
  resumeAssignedCreatives,
  buildAssignedCreativesFetchKey,
  fetchAssignedCreativesForScopes,
  buildMultiScopeFetchKey
} from "../../actions/dcm/assignedCreatives";
import TableToolbar from "./TableToolbar";
import { useHistory } from "react-router-dom";
import { fetchSizeService } from "../../services/dcm/size";
import {
  isDisplayCompatibility,
  buildSiteServedIds,
  toIdSet
} from "../../utils/creativeMatching";

// Dimensionless creative types have no meaningful size — filtering the
// picker by a placement's size only makes sense for DISPLAY-like types.
const DIMENSIONLESS_TYPES = new Set([
  "INSTREAM_VIDEO",
  "INSTREAM_AUDIO",
  "TRACKING_TEXT"
]);

// Placement.compatibility a given dimensionless type corresponds to.
// TRACKING_TEXT has no placement-compatibility equivalent (Site-Served
// placements report compatibility "DISPLAY") and is never combined with
// another dimensionless type in this codebase, so it's exempted from the
// "does a matching placement exist" gate below.
const COMPATIBILITY_FOR_TYPE = {
  INSTREAM_VIDEO: "IN_STREAM_VIDEO",
  INSTREAM_AUDIO: "IN_STREAM_AUDIO"
};

// Splits creativeTypes into independent (types, sizes) fetch scopes.
// CM360's creatives.list filter ANDs `types` with `sizeIds` in one request,
// so a single call can't express "DISPLAY at size X, OR any video, OR any
// audio" — that requires one call per group.
//
// Smart Upload always passes the full ["DISPLAY","INSTREAM_VIDEO",
// "INSTREAM_AUDIO"] set regardless of what's actually selected (it has to,
// since the upload side accepts any file type) — so a group is only
// included here if a placement of matching compatibility was actually
// selected. Without that gate, selecting a single 800x600 placement would
// still fetch every video and every audio creative account-wide.
//
// Every existing single-type stepper passes exactly one group whose
// placements are already guaranteed to match it, so `isMixedRequest` is
// false there and this collapses to the original single-scope behavior,
// unconditionally including that group.
export const computeFetchScopes = (creativeTypes, placements, siteServedIds) => {
  const dimensionlessTypes = creativeTypes.filter((t) => DIMENSIONLESS_TYPES.has(t));
  const displayLikeTypes = creativeTypes.filter((t) => !DIMENSIONLESS_TYPES.has(t));
  const groupCount = (displayLikeTypes.length > 0 ? 1 : 0) + dimensionlessTypes.length;
  const isMixedRequest = groupCount > 1;

  const scopes = [];
  const safePlacements = placements || [];
  const siteServed = toIdSet(siteServedIds);
  const isSiteServed = (p) => siteServed.has(String(p.id));

  if (displayLikeTypes.length > 0) {
    // Site-served placements are excluded from the display scope: nothing
    // display-like can run on them, so their size must not widen the fetch.
    const displayPlacements = safePlacements.filter(
      (p) =>
        !isMixedRequest ||
        (isDisplayCompatibility(p.compatibility) && !isSiteServed(p))
    );
    if (!isMixedRequest || displayPlacements.length > 0) {
      scopes.push({
        creativeTypes: displayLikeTypes,
        sizes: displayPlacements.map((p) => p.size.id)
      });
    }
  }

  for (const type of dimensionlessTypes) {
    let hasMatchingPlacement;

    if (type === "TRACKING_TEXT") {
      // Gated on an actual site-served selection. TRACKING_TEXT has no
      // placement-compatibility equivalent, so the generic check below would
      // include it unconditionally — and the account holds 182k active
      // tracking creatives, which is not a list to pull for every session.
      // Single-type callers (the Site Served flow) are unaffected.
      hasMatchingPlacement =
        !isMixedRequest || safePlacements.some(isSiteServed);
    } else {
      const requiredCompatibility = COMPATIBILITY_FOR_TYPE[type];
      hasMatchingPlacement =
        !isMixedRequest ||
        !requiredCompatibility ||
        safePlacements.some((p) => p.compatibility === requiredCompatibility);
    }

    if (hasMatchingPlacement) {
      scopes.push({ creativeTypes: [type], sizes: [] });
    }
  }

  return scopes;
};

// Mirrors utils/dataUtils.searchDataByField's two-branch matching (exact-id
// match only in the single-term path, substring match otherwise) but also
// checks the rendered Type column label, since that's derived (not a raw
// field on the creative) and the shared utility only takes field names.
const searchCreativesByNameIdOrType = (creatives, searchTerm) => {
  if (!searchTerm) return creatives;

  // Same pasted-ID-column gesture the placement search supports.
  const idList = parseIdList(searchTerm);
  if (idList) {
    const wanted = new Set(idList);
    return creatives.filter((creative) => wanted.has(String(creative.id)));
  }

  if (searchTerm.includes(",")) {
    const terms = searchTerm
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t !== "");

    if (terms.length === 0) return creatives;

    return creatives.filter((creative) => {
      const name = (creative.name || "").toLowerCase();
      const typeLabel = getCreativeTypeLabel(creative.type, null, creative.name).toLowerCase();
      return terms.every((term) => name.includes(term) || typeLabel.includes(term));
    });
  }

  const term = searchTerm.trim().toLowerCase();
  return creatives.filter((creative) => {
    if (String(creative.id).toLowerCase() === term) return true;
    const name = (creative.name || "").toLowerCase();
    const typeLabel = getCreativeTypeLabel(creative.type, null, creative.name).toLowerCase();
    return name.includes(term) || typeLabel.includes(term);
  });
};

const useCreativeListStyles = makeStyles((theme) => ({
  grid: {
    marginTop: theme.spacing(2)
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(2, 4, 3),
    marginBottom: theme.spacing(2)
  }
}));

export default forwardRef(
  (
    { creativeTypes, selectedCreatives, setSelectedCreatives, placements },
    ref
  ) => {
    const classes = useCreativeListStyles();
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [searchText, setSearchText] = React.useState("");
    const dispatch = useDispatch();
    const stableDispatch = useCallback((args) => dispatch(args), [dispatch]);
    const isLoading = useSelector(
      (state) => state.dcmAssignedCreatives.isLoading
    );
    const isLoadingMore = useSelector(
      (state) => state.dcmAssignedCreatives.isLoadingMore
    );
    let dcmCreatives = useSelector((state) =>
      state.dcmAssignedCreatives.allIds.map(
        (id) => state.dcmAssignedCreatives.byId[id]
      )
    );
    const previousFetchKey = useSelector(
      (state) => state.dcmAssignedCreatives.fetchKey
    );
    const previousNextPageToken = useSelector(
      (state) => state.dcmAssignedCreatives.nextPageToken
    );
    const previousIsComplete = useSelector(
      (state) => state.dcmAssignedCreatives.isComplete
    );
    let campaigns = useSelector((state) => state.dcmCampaigns);
    // Site-served placements take tracking creatives and nothing else, so
    // the picker needs to know which ones are before it decides what to
    // fetch. Read here rather than drilled through the dialog.
    const placementInfoById = useSelector((state) => state.placementInfo.byId);
    const siteServedIds = useMemo(
      () => buildSiteServedIds(placementInfoById),
      [placementInfoById]
    );

    const history = useHistory();
    let routeName = history.location.pathname; //after the split dimensions is the second element

    dcmCreatives = searchCreativesByNameIdOrType(dcmCreatives, searchText);

    useEffect(() => {
      let advertiserId = "";

      if (campaigns.selectedId.length > 0) {
        advertiserId = campaigns.byId[campaigns.selectedId].advertiserId;
      }

      const scopes = computeFetchScopes(creativeTypes, placements, siteServedIds);

      if (scopes.length === 0) {
        // No placement matches any requested type/group (shouldn't happen
        // in practice — Smart Upload requires at least one selection) —
        // nothing worth fetching. Falling through would otherwise resolve
        // to the full unfiltered creativeTypes array with no size scoping.
        return;
      }

      // AbortController stops the background paginated fetch when the dialog
      // closes — without this, the thunk's while-loop continues fetching pages
      // after the user picks a creative and closes the picker.
      const controller = new AbortController();

      if (scopes.length > 1) {
        // Mixed placement compatibility/size (Smart Upload) — needs a
        // separate type/size-scoped request per group. No per-scope resume
        // support; a mid-load abort-and-reopen just restarts from scope 0.
        const desiredFetchKey = buildMultiScopeFetchKey(scopes, advertiserId);
        const conditionsMatch = previousFetchKey === desiredFetchKey;

        if (conditionsMatch && previousIsComplete) {
          return;
        }

        stableDispatch(
          fetchAssignedCreativesForScopes("all", scopes, advertiserId, controller.signal)
        );
        return () => controller.abort();
      }

      // Single scope — original behavior, including resume-after-abort.
      // Use the scope's own (possibly narrowed) creativeTypes, not the raw
      // prop — for Smart Upload with e.g. only an audio placement selected,
      // computeFetchScopes narrows creativeTypes down to ["INSTREAM_AUDIO"]
      // even though the prop is still the full 3-type set; fetching with
      // the unnarrowed prop would reintroduce an unbounded, unfiltered
      // fetch. Every existing single-type stepper's scope.creativeTypes is
      // already identical to the prop, so this is a no-op there.
      const scopeTypes = scopes[0].creativeTypes;
      const sizes = scopes[0].sizes;
      const desiredFetchKey = buildAssignedCreativesFetchKey(scopeTypes, sizes, advertiserId);
      const conditionsMatch = previousFetchKey === desiredFetchKey;

      if (conditionsMatch && previousIsComplete) {
        // Same picker, fully loaded last time — nothing to do.
        return;
      }

      if (conditionsMatch && !previousIsComplete && previousNextPageToken) {
        // Same picker, but the previous load was interrupted. Resume
        // from the saved page token without wiping the existing list.
        stableDispatch(
          resumeAssignedCreatives(
            "all",
            scopeTypes,
            sizes,
            advertiserId,
            controller.signal,
            previousNextPageToken
          )
        );
        return () => controller.abort();
      }

      // Different conditions, or no prior data: fresh fetch.
      stableDispatch(
        fetchAssignedCreatives("all", scopeTypes, sizes, advertiserId, controller.signal)
      );
      return () => controller.abort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const controller = new AbortController();
      const getSizeId = async () => {
        let onDeufaultRoute = routeName.split('/default/').length > 1;
        let advertiserId = "";
        let isVideo = false;
        let sizes = [];

        if (onDeufaultRoute) {
          let dimensions = routeName.split('/default/')[1].split('x');
          let dcmSize = await fetchSizeService(dimensions[0], dimensions[1])
          if (controller.signal.aborted) return;
          sizes = [dcmSize.id];
          if (campaigns.selectedId.length > 0) {
            advertiserId = campaigns.byId[campaigns.selectedId].advertiserId;
          }

          if (dimensions[0] === "0" && dimensions[1] === "0") {
            isVideo = true
            sizes = []
          }

          stableDispatch(
            fetchAssignedCreatives("all", isVideo ? ["INSTREAM_VIDEO"] : creativeTypes, sizes, advertiserId, controller.signal)
          );
        }
      }
      getSizeId();
      return () => controller.abort();
    }, [campaigns.byId, campaigns.selectedId, creativeTypes, stableDispatch, routeName])

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(+event.target.value);
      setPage(0);
    };

    const handleSelectCreative = (creative) => {
      const selectedIndex = selectedCreatives.indexOf(creative);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedCreatives, creative);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedCreatives.slice(1));
      } else if (selectedIndex === selectedCreatives.length - 1) {
        newSelected = newSelected.concat(selectedCreatives.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedCreatives.slice(0, selectedIndex),
          selectedCreatives.slice(selectedIndex + 1)
        );
      }

      setSelectedCreatives(newSelected);
    };

    // const isSelected = (creative) => selectedCreatives.indexOf(creative) !== -1;
    const isSelected = (creative) => {
      let foundCreative = selectedCreatives.find((c) => c.id === creative.id);

      if (foundCreative) {
        return true;
      } else {
        return false;
      }
    };

    const handleSelectAllClick = (event) => {
      if (event.target.checked) {
        const newSelectedCreatives = dcmCreatives;
        setSelectedCreatives(newSelectedCreatives);
        return;
      }
      setSelectedCreatives([]);
    };

    useImperativeHandle(ref, () => ({
      validate() {
        if (selectedCreatives.length === 0) {
          return {
            hasErrors: true,
            message: "You need to add at least one creative."
          };
        }

        return {
          hasErrors: false,
          message: ""
        };
      }
    }));

    return (
      <Grid container>
        <Grid item xs={12} className={classes.grid}>
          <Paper className={classes.paper}>
            <TableToolbar
              numSelected={selectedCreatives.length}
              onSearch={(e) => setSearchText(e.target.value)}
              onClear={() => setSearchText("")}
              searchTerm={searchText}
            />
            <Table style={{ tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 48 }} />
                <col style={{ width: "15%" }} />
                <col />
                <col style={{ width: 110 }} />
                <col style={{ width: 120 }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      onClick={(event) => handleSelectAllClick(event)}
                      indeterminate={
                        selectedCreatives.length > 0 &&
                        selectedCreatives < dcmCreatives.length
                      }
                      checked={
                        selectedCreatives.length > 0 &&
                        selectedCreatives.length === dcmCreatives.length
                      }
                    />
                  </TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Dimensions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress
                        style={{
                          position: "relative",
                          margin: "2vh 0 2vh 0"
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  dcmCreatives
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((creative, index) => {
                      return (
                        <TableRow key={`creative-list-${index}`}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected(creative)}
                              onClick={(event) =>
                                handleSelectCreative(creative)
                              }
                            />
                          </TableCell>
                          <TableCell>{creative.id}</TableCell>
                          <TableCell style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{creative.name}</TableCell>
                          <TableCell>{getCreativeTypeLabel(creative.type, null, creative.name)}</TableCell>
                          <TableCell align="right">{`${
                            creative.size.width === 0 &&
                            creative.type === "TRACKING_TEXT"
                              ? 1
                              : creative.size.width
                          } x ${
                            creative.size.height === 0 &&
                            creative.type === "TRACKING_TEXT"
                              ? 1
                              : creative.size.height
                          }`}</TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
            {isLoadingMore && (
              <div style={{ padding: "8px 16px" }}>
                <LinearProgress />
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                  Loading more creatives... ({dcmCreatives.length} loaded)
                </Typography>
              </div>
            )}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={dcmCreatives.length}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{
                "aria-label": "previous page"
              }}
              nextIconButtonProps={{
                "aria-label": "next page"
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>
    );
  }
);
